import { useEffect, useMemo, useRef, useState } from 'react';
import { pollUntil } from '../lib/contestApi';
import { serverNow } from '../stores/serverClock';
import { LANGUAGES } from '../lib/languages';
import { storage } from '../lib/storage';

const LANGUAGE_KEY = 'code-language';
const RUN_COOLDOWN_MS = 3000;
const SUBMIT_COOLDOWN_MS = 5000;
const DRAFT_SAVE_DELAY_MS = 500;
const ALL_LANGUAGES = LANGUAGES.map((item) => item.value);

export function useCodeWorkspace({ draftPrefix, problemId, problem, canAct, now, api, onRequestError, onJudged }) {
  const [language, setLanguage] = useState(() => storage.get(LANGUAGE_KEY) || 'cpp');
  const [codes, setCodes] = useState({});
  const [cases, setCases] = useState({});
  const [runs, setRuns] = useState({});
  const [judgements, setJudgements] = useState({});
  const [lastActions, setLastActions] = useState({});
  const [consoleTab, setConsoleTab] = useState('testcases');
  const [submissionsVersion, setSubmissionsVersion] = useState(0);
  const [cooldowns, setCooldowns] = useState({ run: 0, submit: 0 });
  const [savedAt, setSavedAt] = useState(null);
  const pollers = useRef(new Set());

  useEffect(() => {
    const active = pollers.current;
    return () => active.forEach((controller) => controller.abort());
  }, []);

  const languages = useMemo(() => {
    if (!problem) return ALL_LANGUAGES;
    const available = ALL_LANGUAGES.filter((value) => typeof problem.sourceCode[value] === 'string');
    return available.length > 0 ? available : ALL_LANGUAGES;
  }, [problem]);
  const effectiveLanguage = languages.includes(language) ? language : languages[0];

  const codeKey = problemId !== null ? `${problemId}:${effectiveLanguage}` : null;
  const draftKey = problemId !== null ? `${draftPrefix}:draft:${problemId}:${effectiveLanguage}` : null;
  const template = problem?.sourceCode?.[effectiveLanguage] ?? '';
  const code = codeKey && codes[codeKey] !== undefined ? codes[codeKey] : template;

  useEffect(() => {
    if (!problem || codeKey === null || codes[codeKey] !== undefined) return;
    const saved = storage.get(draftKey);
    setCodes((current) => ({ ...current, [codeKey]: saved ?? template }));
  }, [problem, codeKey, codes, draftKey, template]);

  useEffect(() => {
    if (codeKey === null || codes[codeKey] === undefined) return undefined;
    const value = codes[codeKey];
    const timer = setTimeout(() => {
      if (storage.set(draftKey, value)) setSavedAt(Date.now());
    }, DRAFT_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [codes, codeKey, draftKey]);

  const samples = useMemo(
    () =>
      problem?.sampleTests?.length
        ? problem.sampleTests.map((sample) => ({ input: sample.input, output: sample.output }))
        : [{ input: '', output: '' }],
    [problem]
  );
  const testCases = problemId !== null && cases[problemId] ? cases[problemId] : samples;

  const run = problemId !== null ? runs[problemId] : undefined;
  const judgement = problemId !== null ? judgements[problemId] : undefined;
  const lastAction = problemId !== null ? lastActions[problemId] : undefined;
  const running = run?.status === 'pending';
  const judging = judgement?.status === 'pending';
  const runCooldownMs = Math.max(0, cooldowns.run - now);
  const submitCooldownMs = Math.max(0, cooldowns.submit - now);

  const track = (controller) => {
    pollers.current.add(controller);
    return () => pollers.current.delete(controller);
  };

  const applyCooldown = (kind, response, fallbackMs) => {
    const ms = typeof response?.retryAfterMs === 'number' ? response.retryAfterMs : fallbackMs;
    setCooldowns((current) => ({ ...current, [kind]: serverNow() + ms }));
  };

  const handleRun = async () => {
    if (!canAct || problemId === null || !problem || running || judging || runCooldownMs > 0) return;
    const id = problemId;
    const snapshot = testCases.map((testCase) => ({ input: testCase.input, output: testCase.output }));

    setLastActions((current) => ({ ...current, [id]: 'run' }));
    setConsoleTab('result');
    setRuns((current) => ({ ...current, [id]: { status: 'pending', cases: snapshot } }));
    applyCooldown('run', null, RUN_COOLDOWN_MS);

    const response = await api.run(id, { code, language: effectiveLanguage, testCases: snapshot });
    if (!response.success) {
      if (response.status === 429) applyCooldown('run', response, RUN_COOLDOWN_MS);
      setRuns((current) => ({ ...current, [id]: { status: 'error', error: response.error } }));
      onRequestError?.(response);
      return;
    }

    const controller = new AbortController();
    const untrack = track(controller);
    try {
      const result = await pollUntil(
        () => api.runStatus(response.data.token),
        (data) => data?.status?.id > 2,
        { signal: controller.signal }
      );
      setRuns((current) => ({ ...current, [id]: { status: 'done', result, cases: snapshot } }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        setRuns((current) => ({ ...current, [id]: { status: 'error', error: err.message } }));
      }
    } finally {
      untrack();
    }
  };

  const handleSubmit = async () => {
    if (!canAct || problemId === null || !problem || judging || running || submitCooldownMs > 0) return;
    const id = problemId;

    setLastActions((current) => ({ ...current, [id]: 'submit' }));
    setConsoleTab('result');
    setJudgements((current) => ({ ...current, [id]: { status: 'pending' } }));
    applyCooldown('submit', null, SUBMIT_COOLDOWN_MS);

    const response = await api.submit(id, { code, language: effectiveLanguage });
    if (!response.success) {
      if (response.status === 429) applyCooldown('submit', response, SUBMIT_COOLDOWN_MS);
      setJudgements((current) => ({ ...current, [id]: { status: 'error', error: response.error } }));
      onRequestError?.(response);
      return;
    }

    setSubmissionsVersion((value) => value + 1);
    const controller = new AbortController();
    const untrack = track(controller);
    try {
      const result = await pollUntil(
        () => api.submissionStatus(response.data.submissionId),
        (data) => data?.status === 'DONE' || data?.status === 'FAILED',
        { signal: controller.signal }
      );
      setJudgements((current) => ({ ...current, [id]: { status: 'done', result } }));
      setSubmissionsVersion((value) => value + 1);
      onJudged?.(id, result);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setJudgements((current) => ({ ...current, [id]: { status: 'error', error: err.message } }));
      }
    } finally {
      untrack();
    }
  };

  const changeLanguage = (value) => {
    setLanguage(value);
    storage.set(LANGUAGE_KEY, value);
  };

  const updateCode = (value) => {
    if (codeKey === null) return;
    setCodes((current) => ({ ...current, [codeKey]: value }));
  };

  const resetCode = () => {
    if (codeKey === null) return;
    setCodes((current) => ({ ...current, [codeKey]: template }));
  };

  const updateCases = (next) => {
    if (problemId === null) return;
    setCases((current) => ({ ...current, [problemId]: next }));
  };

  const resetCases = () => {
    if (problemId === null) return;
    setCases((current) => {
      const next = { ...current };
      delete next[problemId];
      return next;
    });
  };

  return {
    language: effectiveLanguage,
    languages,
    changeLanguage,
    code,
    updateCode,
    resetCode,
    savedAt,
    testCases,
    updateCases,
    resetCases,
    run,
    judgement,
    lastAction,
    consoleTab,
    setConsoleTab,
    submissionsVersion,
    running,
    judging,
    runCooldownMs,
    submitCooldownMs,
    handleRun,
    handleSubmit,
  };
}
